import * as React from 'react';
import { Box, type SxProps, type Theme, useTheme } from '@mui/material'
import { useDrag } from '@workwave-tidal/core/hooks/useDrag'
import { type PropsWithChildren, useEffect } from 'react'
import { ConversationPanelHandle } from './components/ConversationPanelHandle.js'
import { useReferenceSize } from './hooks/useReferenceSize.js'

export type ConversationPanelProps = PropsWithChildren<{
  initialHeight: number
  initialWidth: number
  minHeight?: number
  minWidth?: number
  maxHeight?: number
  maxWidth?: number
  sx?: SxProps<Theme>
  cornerSize?: number
  handleSize?: number
  onChangeSize?: (width: number, height: number) => void
  fullScreen?: boolean
  hidden?: boolean
  fullScreenHorizontalMarginUnits?: number
  fullScreenVerticalMarginUnits?: number

  /**
   * element used for referencing the full screen
   * @default document.body
   * @description This is used to calculate the full screen size
   */
  fullScreenElementReference?: HTMLElement
}>

const disableDragAndDrop = () => false

export function ConversationPanel(props: ConversationPanelProps) {
  const {
    sx,
    children,
    fullScreen,
    cornerSize = 14,
    handleSize = 4,
    onChangeSize,
    initialHeight,
    initialWidth,
    minHeight = 0,
    minWidth = 0,
    maxHeight,
    maxWidth,
    hidden,
    fullScreenHorizontalMarginUnits = 6,
    fullScreenVerticalMarginUnits = 20,
    fullScreenElementReference = document.body,
  } = props

  const { spacing } = useTheme()

  const acceptEvent = fullScreen ? disableDragAndDrop : undefined

  // ------------------------------
  // Corner drag
  // ------------------------------

  const {
    dragInfo: { offsetX: offsetXCorner, offsetY: offsetYCorner },
    dragRef: dragRefCorner,
  } = useDrag<HTMLDivElement>({ persist: true, acceptEvent })

  // ------------------------------
  // Top drag
  // ------------------------------

  const {
    dragInfo: { offsetY: offsetYTop },
    dragRef: dragRefTop,
  } = useDrag<HTMLDivElement>({ persist: true, acceptEvent })

  // ------------------------------
  // Left drag
  // ------------------------------

  const {
    dragInfo: { offsetX: offsetXLeft },
    dragRef: dragRefLeft,
  } = useDrag<HTMLDivElement>({ persist: true, acceptEvent })

  const referenceSize = useReferenceSize(fullScreenElementReference)

  // ------------------------------
  // Calculate offset
  // ------------------------------

  const offsetX = offsetXCorner + offsetXLeft
  const offsetY = offsetYCorner + offsetYTop
  const width = initialWidth - offsetX
  const height = initialHeight - offsetY

  const fullScreenWidth = `calc(${referenceSize.width}px - ${spacing(fullScreenHorizontalMarginUnits)})`
  const fullScreenHeight = `calc(${referenceSize.height}px - ${spacing(fullScreenVerticalMarginUnits)})`

  const boxStyle = fullScreen
    ? { width: fullScreenWidth, height: fullScreenHeight }
    : { width, height, minHeight, minWidth, maxHeight, maxWidth }

  // ------------------------------
  // Notify parent of size change
  // ------------------------------

  useEffect(() => {
    if (typeof width === 'string' || typeof height === 'string') return
    onChangeSize?.(width, height)
  }, [width, height, onChangeSize])

  if (hidden) return null

  return (
    <Box sx={sx} position="relative" display="flex" style={boxStyle}>
      {children}

      <ConversationPanelHandle
        ref={dragRefCorner}
        sx={{
          width: cornerSize,
          height: cornerSize,
          top: 0,
          left: 0,
          cursor: fullScreen ? 'default' : 'nwse-resize',
        }}
      />

      <ConversationPanelHandle
        ref={dragRefTop}
        sx={{
          height: handleSize,
          top: 0,
          left: 0,
          cursor: fullScreen ? 'default' : 'ns-resize',
        }}
      />

      <ConversationPanelHandle
        ref={dragRefLeft}
        sx={{
          width: handleSize,
          top: 0,
          bottom: 0,
          cursor: fullScreen ? 'default' : 'ew-resize',
        }}
      />
    </Box>
  )
}
